/**
 * Which models a client may run, and which it actually runs.
 *
 * Pure — it reads the shared vocabulary and nothing else. That matters: the
 * Evaluate wizard and the quick run need these answers, and routing them
 * through the `data/targets` seam would drag the whole client data layer (and
 * `data/user`, and `data/project`) into every test that renders a summary.
 */
import {
  MODEL_PROVIDER,
  SPHINX_MODELS,
  TARGET_PROVIDER,
  type SphinxModel,
} from "@/shared/sphinx";

/**
 * Models a client may run: family-pinned through the same two shared tables
 * the server checks (`TARGET_PROVIDER` × `MODEL_PROVIDER`), so no surface
 * offers a pick a run would drop. Anthropic clients → the claude models,
 * OpenAI clients → the gpt models, and that includes the two direct-API
 * clients (`anthropic-api` cannot run a GPT model).
 */
export function modelsForClient(target: string): readonly SphinxModel[] {
  const provider = (TARGET_PROVIDER as Record<string, "anthropic" | "openai">)[
    target
  ];
  if (!provider) return SPHINX_MODELS;

  return SPHINX_MODELS.filter((model) => MODEL_PROVIDER[model] === provider);
}

const isSphinxModel = (value: unknown): value is SphinxModel =>
  typeof value === "string" && (SPHINX_MODELS as readonly string[]).includes(value);

/**
 * What a client actually runs, in the order the server expands it: its
 * `models` list (migration 012), falling back to the legacy single `model`.
 * Mirrors `modelsForTarget` in `server/queue/enqueue.ts` — a client with two
 * models produces two iterations per case, which is what the Evaluate summary
 * counts.
 */
export function effectiveModels(target: {
  models?: readonly string[] | null;
  model?: string | null;
}): SphinxModel[] {
  const list = (target.models ?? []).filter(isSphinxModel);
  if (list.length > 0) return list;

  return isSphinxModel(target.model) ? [target.model] : [];
}
